import { FC, MouseEvent, useEffect, useState } from 'react';

import DescriptionIcon from '@mui/icons-material/Description';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Collapse, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';

import placeholderImage from '@/assets/adv_space_placeholder_large.png';
import PageIcon from '@/assets/icons/PageIcon.svg';

import {
  CardContainer,
  DetailsContainer,
  FileNameText,
  FileThumbnail,
  LabelText,
  UploaderBadge,
  ValueText,
} from './FileCard.styles';

export interface FileData {
  id: number;
  category: string;
  thumbnail: string;
  title: string;
  displayDate: string;
  realFileName: string;
  fileType: string;
  uploadDate: string;
  uploaderName: string;
  uploaderInitial: string;
}

interface FileCardProps {
  file: FileData;
  active: boolean;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  hasDeleteOption?: boolean;
}

const FileCard: FC<FileCardProps> = ({
  file,
  active,
  onSelect,
  onDelete,
  hasDeleteOption = true,
}) => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const [hasImageLoadError, setHasImageLoadError] = useState(false);

  const isPdf = file.fileType === 'PDF' || file.realFileName?.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    setHasImageLoadError(false);
  }, [file.thumbnail]);

  const handleImageLoadError = () => {
    setHasImageLoadError(true);
  };

  const displayImageSource =
    hasImageLoadError || !file.thumbnail ? placeholderImage : file.thumbnail;

  const isPlaceholderImage = displayImageSource === placeholderImage;
  const isMenuOpen = Boolean(menuAnchorElement);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorElement(null);
  };

  const handleDeleteFile = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onDelete(file.id);
    setMenuAnchorElement(null);
  };

  const handleToggleCardExpansion = () => {
    onSelect(file.id);
    setIsCardExpanded(!isCardExpanded);
  };

  return (
    <CardContainer elevation={0} onClick={handleToggleCardExpansion} isActive={active}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="0.75rem" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          {isPdf ? (
            <FileThumbnail src={PageIcon} alt="PDF Icon" isPlaceholder={false} />
          ) : (
            <FileThumbnail
              src={displayImageSource}
              onError={handleImageLoadError}
              alt={file.title}
              isPlaceholder={isPlaceholderImage}
            />
          )}

          <Box display="flex" flexDirection="column" gap="0.25rem" sx={{ minWidth: 0 }}>
            <FileNameText>{file.title}</FileNameText>

            <Box display="flex" alignItems="center" gap="0.5rem">
              <UploaderBadge>{file.uploaderInitial}</UploaderBadge>
              <Typography sx={{ fontSize: '0.75rem', color: '#615E6F' }}>
                {file.displayDate}
              </Typography>
            </Box>
          </Box>
        </Box>
        {hasDeleteOption && (
          <IconButton
            size="small"
            onClick={handleOpenMenu}
            sx={{ color: '#615E6F', ml: '4px', flexShrink: 0 }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={isCardExpanded} timeout="auto" unmountOnExit>
        <DetailsContainer>
          <LabelText>File Name:</LabelText>
          <ValueText textAlign="right" noWrap>
            {file.realFileName}
          </ValueText>

          <LabelText>File Type:</LabelText>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap="4px">
            <DescriptionIcon
              sx={(theme) => ({ fontSize: '1rem', color: theme.palette.accent.main })}
            />
            <ValueText>{file.fileType}</ValueText>
          </Box>

          <LabelText>Upload Date:</LabelText>
          <ValueText textAlign="right">{file.uploadDate}</ValueText>

          <LabelText alignSelf="center">Uploaded by:</LabelText>
          <ValueText textAlign="right">{file.uploaderName}</ValueText>
        </DetailsContainer>
      </Collapse>

      <Menu
        anchorEl={menuAnchorElement}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: '0.5rem', mt: 0.5, minWidth: '120px' },
        }}
      >
        <MenuItem onClick={handleDeleteFile} sx={{ color: '#615E6F', fontSize: '0.8rem' }}>
          <ListItemIcon>Delete file</ListItemIcon>
        </MenuItem>
      </Menu>
    </CardContainer>
  );
};

export default FileCard;
